// The bottom input bar: textarea + send / interactive / push-to-talk controls, plus file attach.
// Mode availability is gated by `modesAllowed`. While a push-to-talk recording is active the whole
// row is replaced by the recorder bar (PttRecorder). The PTT mic is the LAST control, after the
// interactive (waveform) button.
import { useRef } from 'react';
import { WaveformIcon, PlusIcon, SendIcon } from '@/components/chat/ChatIcons';
import { AudioMoreMenu, type AudioMoreMenuProps } from './AudioMoreMenu';
import { PttMicButton, PttRecorder, type PttControls } from './PttRecorder';
import type { UploadedFile } from '@/redux/internal-services/agentApi';

export const MessageComposer = ({
  input,
  onInputChange,
  onKeyDown,
  onSend,
  modesAllowed,
  onToggleInteractive,
  onEnterHandsFree,
  inVoice = false,
  attachments = [],
  onAddFiles,
  onRemoveAttachment,
  uploading = false,
  attachError = null,
  fileAccept,
  uploadEnabled = true,
  moreMenu,
  ptt,
}: {
  input: string;
  onInputChange: (value: string) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onSend: () => void;
  modesAllowed: string[];
  onToggleInteractive: () => void;
  /** Long-press (4s) the interactive/waveform button to jump straight into hands-free mode.
   *  When omitted (e.g. desktop, where hands-free is unavailable) the button is tap-only. */
  onEnterHandsFree?: () => void;
  /** During a voice session: drop the mode-entry buttons (PTT / waveform); End lives in VoicePills. */
  inVoice?: boolean;
  /** Staged attachments (text/PTT). Interactive mode dispatches files immediately, so none stage here. */
  attachments?: UploadedFile[];
  onAddFiles?: (files: FileList) => void;
  onRemoveAttachment?: (url: string) => void;
  uploading?: boolean;
  attachError?: string | null;
  /** `accept` attribute built from the allowed extensions. */
  fileAccept?: string;
  uploadEnabled?: boolean;
  /** Captions + mic/speaker device picker (⋮). Shown only for voice-capable agents. */
  moreMenu?: AudioMoreMenuProps;
  /** Push-to-talk recorder state + actions (hold→lock→swipe). */
  ptt?: PttControls;
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canSend = !!input.trim() || attachments.length > 0;

  // Interactive-button press handling: a short tap toggles interactive voice; holding it for 4s
  // switches straight into hands-free mode (when available). Implemented with pointer events so it
  // works for both mouse and touch; the timer is cleared if the press ends or leaves early.
  const HOLD_MS = 1000;
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressFired = useRef(false);
  const clearHold = () => {
    if (holdTimer.current) { clearTimeout(holdTimer.current); holdTimer.current = null; }
  };
  const startHold = () => {
    longPressFired.current = false;
    if (!onEnterHandsFree) return; // no long-press target → button stays tap-only
    holdTimer.current = setTimeout(() => {
      longPressFired.current = true;
      holdTimer.current = null;
      onEnterHandsFree();
    }, HOLD_MS);
  };
  const endHold = () => {
    const fired = longPressFired.current;
    clearHold();
    if (!fired) onToggleInteractive(); // released before 4s → treat as a normal tap
  };
  // Interactive voice session: all interaction is by voice and every control (mic / speaker / End)
  // lives in VoicePills above this bar, so hide the typing + file-attach bar entirely.
  if (inVoice) return null;
  // Active push-to-talk recording: the recorder bar (waveform + timer + delete/pause/send) takes
  // over the whole row.
  if (ptt?.recording) return <PttRecorder {...ptt} />;
  return (
    <div className="flex-shrink-0 px-4 pt-2 border-t border-ui-border" style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}>
      {(attachments.length > 0 || uploading || attachError) && (
        <div className="flex flex-wrap items-center gap-2 mb-2">
          {attachments.map((f) => (
            <span key={f.url} className="flex items-center gap-1.5 max-w-[200px] bg-ui-subtle border border-ui-border rounded-lg px-2 py-1 text-xs text-ui-text">
              <span className="truncate">{f.name}</span>
              <button aria-label={`Remove ${f.name}`} onClick={() => onRemoveAttachment?.(f.url)} className="text-ui-text2 hover:text-ui-text flex-shrink-0">✕</button>
            </span>
          ))}
          {uploading && <span className="text-xs text-ui-text2 animate-pulse">Uploading…</span>}
          {attachError && <span className="text-xs text-danger">{attachError}</span>}
        </div>
      )}
      <div className="flex items-end gap-2 bg-ui-subtle border border-ui-border rounded-2xl px-3 py-2">
        {uploadEnabled && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={fileAccept}
              className="hidden"
              onChange={(e) => { if (e.target.files?.length) onAddFiles?.(e.target.files); e.target.value = ''; }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              title="Attach files"
              className="w-9 h-9 rounded-full flex items-center justify-center text-ui-text2 hover:text-ui-text transition flex-shrink-0 mb-0.5"
            >
              <PlusIcon className="h-5 w-5" />
            </button>
          </>
        )}
        <textarea
          value={input}
          onChange={e => onInputChange(e.target.value)}
          onKeyDown={onKeyDown}
          rows={1}
          placeholder="Type a message"
          className="flex-1 resize-none outline-none text-sm py-1.5 max-h-28 overflow-y-hidden bg-transparent text-ui-text placeholder:text-ui-text3 leading-relaxed"
        />
        {moreMenu && (modesAllowed.includes('push_to_talk') || modesAllowed.includes('interactive')) && (
          <AudioMoreMenu {...moreMenu} />
        )}
        {canSend ? (
          <button aria-label="Send" onClick={onSend} className="w-9 h-9 rounded-full bg-brand-dark flex items-center justify-center text-white hover:bg-brand-dark/80 transition flex-shrink-0">
            <SendIcon className="h-5 w-5" />
          </button>
        ) : modesAllowed.includes('interactive') ? (
          <button
            onPointerDown={startHold}
            onPointerUp={endHold}
            onPointerLeave={clearHold}
            onPointerCancel={clearHold}
            title={onEnterHandsFree ? 'Interactive voice — hold 4s for hands-free' : 'Interactive voice'}
            className="w-9 h-9 rounded-full flex items-center justify-center transition flex-shrink-0 text-ui-text2 hover:text-ui-text select-none touch-none"
          >
            <WaveformIcon className="h-5 w-5" />
          </button>
        ) : null}
        {/* PTT mic — last, after the interactive button. Hold to talk; slide up to lock, left to cancel. */}
        {ptt && modesAllowed.includes('push_to_talk') && (
          <PttMicButton start={ptt.start} send={ptt.send} cancel={ptt.cancel} lock={ptt.lock} />
        )}
      </div>
    </div>
  );
};
